import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { m } from 'motion/react';
import { changelogQuery } from '../api/changelog';
import { abrirInstalador, useModoInstalar } from '../lib/instalacao';
import { DURACAO, EASE_SAIDA, SO_OPACIDADE, useMovimentoReduzido } from '../lib/movimento';
import { Icone } from './Icone';
import { Botao } from './UI';

/*
 * Convite discreto para pôr o Chama Um na tela inicial.
 *
 * Não é modal nem flutua: é um cartão baixo no topo da tela de início, no
 * fluxo da página, que não cobre conteúdo nem a barra de baixo. Só aparece
 * quando dá para instalar (ou explicar como, no iPhone), espera o popup de
 * novidades fechar e um instante depois da tela abrir. "Agora não" some com
 * ele por 30 dias neste aparelho; instalou, some de vez.
 */

const CHAVE = 'chamaum:convite-instalar';
const PAUSA_SEGUNDOS = 30 * 24 * 60 * 60;
const ESPERA_MS = 2500;

function dispensadoRecentemente(): boolean {
  try {
    const em = Number(localStorage.getItem(CHAVE));
    return Number.isFinite(em) && em > 0 && Math.floor(Date.now() / 1000) - em < PAUSA_SEGUNDOS;
  } catch {
    return false;
  }
}

function lembrarDispensa() {
  try {
    localStorage.setItem(CHAVE, String(Math.floor(Date.now() / 1000)));
  } catch {
    // Aba anônima ou armazenamento cheio: o convite volta na próxima visita.
  }
}

export function ConviteInstalar() {
  const modo = useModoInstalar();
  const reduzido = useMovimentoReduzido();
  const { data } = useQuery(changelogQuery);
  const novidadesAbertas = (data?.nao_vistas.length ?? 0) > 0;
  const [dispensado, setDispensado] = useState(dispensadoRecentemente);
  const [esperou, setEsperou] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setEsperou(true), ESPERA_MS);
    return () => window.clearTimeout(t);
  }, []);

  if (!modo || dispensado || !esperou || novidadesAbertas) return null;

  // O botão tocado vai sumir: o foco iria para o <body> e quem usa teclado ou
  // leitor de tela perderia o lugar. Leva para o título da tela.
  function devolverFoco() {
    const titulo = document.querySelector<HTMLElement>('#conteudo h1');
    if (!titulo) return;
    if (!titulo.hasAttribute('tabindex')) titulo.setAttribute('tabindex', '-1');
    titulo.focus();
  }

  function dispensar() {
    lembrarDispensa();
    devolverFoco();
    setDispensado(true);
  }

  async function instalar() {
    const aceitou = await abrirInstalador();
    // Recusou no instalador do navegador: mesmo efeito de "Agora não".
    if (aceitou) devolverFoco();
    else dispensar();
  }

  // Abre a altura junto: o conteúdo de baixo desce devagar em vez de pular
  // debaixo do dedo de quem já ia tocar nele.
  return (
    <m.section
      aria-labelledby="t-convite-instalar"
      className="convite-instalar-moldura"
      initial={reduzido ? { opacity: 0 } : { opacity: 0, height: 0 }}
      animate={reduzido ? { opacity: 1 } : { opacity: 1, height: 'auto' }}
      transition={reduzido ? SO_OPACIDADE : { duration: DURACAO.base, ease: EASE_SAIDA }}
    >
      <div className="convite-instalar">
        <Icone nome="instalar" className="convite-instalar-icone" />
        <div className="convite-instalar-texto">
          <p id="t-convite-instalar" className="font-bold">Tenha o Chama Um na tela inicial</p>
          {modo === 'nativo' ? (
            <p>Abre num toque, como um aplicativo.</p>
          ) : (
            <p>
              Toque em <strong>Compartilhar</strong> e depois em{' '}
              <strong>Adicionar à Tela de Início</strong>.
            </p>
          )}
        </div>
        <div className="convite-instalar-acoes">
          {modo === 'nativo' && <Botao type="button" onClick={() => void instalar()}>Instalar</Botao>}
          <button type="button" className="acao-link" onClick={dispensar}>Agora não</button>
        </div>
      </div>
    </m.section>
  );
}
